import "./horizontal-bar.scss";

import * as React from "react";
import {cx} from "emotion";

import {cs} from "../../../react/chain-services";
import {UseState} from "../../../react/use-state";
import {getHorizontalBarOptions} from "./chart-options/get-horizontal-bar-options";
import {isDataValid, loadTileData} from "../common/load-tile-data";
import {groupCompareBarSeries} from "../bar-series-processors/group-compare-bar-series";
import {consumeContext} from "../../../react/context";
import {getFormatters} from "../factory/formatters/formatters";
import {getTooltipPerSeriesOption} from "../factory/tooltip/tooltip-series-option";
import {isComparing} from "../factory/check-compare";
import {getMeasurementAxesOptions} from "../factory/measurement-axes/measurement-axes";
import {getDimensionAxisOptions} from "../factory/dimension-axis/dimension-axis";
import {isEmpty, keepOnly} from "../../../utils/objects";
import {getDateFilterValue} from "../factory/get-date-filter-value";
import {getFieldType} from "../common/get-field-type";
import {RenderChart} from "../render-chart/render-chart";
import {SwapAxis} from "../common/swap-axis/swap-axis";
import {groupNonCompareBarSeries} from "../bar-series-processors/group-non-compare-bar-series";
import {cascadeFind} from "../../../utils/cascade";
import {MenuOptions} from "../common/menu-options/menu-options";
import {isDimRealDateTime} from "../factory/is-dim-real-date-time";
import {rawDataProcessor} from "../data-processor/raw-data-processor";
import {rawDataManipulator} from "../raw-data-manipulator/raw-data-manipulator";
import {NoDataScreen} from "../common/no-data-screen/no-data-screen";
import {ChartInnerLayout} from "../chart-layout/chart-inner-layout";
import {flatten1} from "../../../utils/collections";
import {ActionMenu} from "../common/action-menu/action-menu";
import {loadTileFields} from "../get-field-color";
import {generateReferenceSeries} from "../reference-lines/generate-reference-series";
import {IgnoreUpdate} from "../../../react/ignore-update";
import {RemoveTileControlFilter} from "../common/control-filter/remove-tile-control-filter";
import {BlankDateWarning} from "../common/blank-date-warning";
import {CurrencyConversionWarning, getCurrencyConversion} from "../common/currency-conversion-warning/currency-conversion-warning";
import {fragments} from "../../../react/fragments";
import {Watch} from "../../../react/watch";

export const HorizontalBar = ({tile, size, tileFilters, loadData, downloadData, theme, chartRef, hasSwapAxis, defaultData, overrideTile, disabledTileActions = false}) =>
    cs(
        consumeContext("sdkStaticUrl"),
        consumeContext("tileActionControlFilter"),
        [
            "chartData",
            ({tileActionControlFilter}, next) =>
                defaultData
                    ? next(defaultData)
                    : loadTileData({
                          next,
                          tileFilters,
                          loadData,
                          size,
                          tile,
                          theme,
                          tileKey: JSON.stringify([keepOnly(tile, ["xAxisFields", "yAxisField", "groupField", "filters", "sort", "limit", "categorySort"]), tile.style.yAxis.dateGaps]),
                          disabled: tileActionControlFilter?.value?.tile?.id == tile.id,
                          removeJSCursor: true,
                      }),
        ],
        consumeContext("selectedTimezone"),
        ["rawData", ({chartData}, next) => next(rawDataManipulator({tile, rawData: chartData}))],

        ({rawData}, next) => (!isDataValid(rawData) ? NoDataScreen({size, theme}) : next()),

        ["isCompare", ({rawData}, next) => next(isComparing(rawData, tile))],
        [
            "isDimensionRealDate",
            ({rawData}, next) =>
                next(
                    isDimRealDateTime({
                        tile,
                        dimensionAttr: "yAxisField",
                        series: rawData.series,
                    })
                ),
        ],

        [
            "rawData2",
            ({isCompare, rawData, isDimensionRealDate}, next) =>
                next(
                    rawDataProcessor({
                        tile,
                        rawData,
                        isDimensionRealDate,
                        isCompare,
                    })
                ),
        ],

        [
            "formatters",
            ({rawData2, selectedTimezone}, next) =>
                next(
                    getFormatters({
                        tile,
                        dimensionAttr: "yAxisField",
                        measurementAttrs: ["xAxisFields"],
                        rawData: rawData2,
                        timezone: selectedTimezone?.value,
                    })
                ),
        ],

        [
            "tooltipOptions",
            ({formatters, isCompare, rawData2, isDimensionRealDate}, next) =>
                next(
                    getTooltipPerSeriesOption({
                        tile,
                        theme,
                        styleMeasurementAttr: "xAxis",
                        formatters,
                        isDimensionDate: getFieldType(tile["yAxisField"]) === "date",
                        isDimensionRealDate,
                        isCompare,
                        dateFilterValue: getDateFilterValue(rawData2.series),
                        isBarClassic: !tile.style.displayType || tile.style.displayType === "Classic",
                        rawData: rawData2,
                    })
                ),
        ],

        [
            "tileFields",
            ({}, next) =>
                loadTileFields({
                    next,
                    configs: {
                        tile,
                        dimensionAttr: "yAxisField",
                        measureGroupAttrs: ["xAxisFields"],
                        groupFieldAttr: "groupField",
                        isBarFields: ["xAxisFields"],
                        checkUnique: false,
                    },
                }),
        ],

        [
            "data",
            ({tooltipOptions, rawData2, formatters, isCompare, tileFields}, next) => {
                const groupFn = isCompare ? groupCompareBarSeries : groupNonCompareBarSeries;
                return next(
                    groupFn({
                        series: rawData2.series,
                        tile,
                        theme,
                        isCompare,
                        tileFields,
                        isDimensionDate: getFieldType(tile["yAxisField"]) === "date",
                        isBarDataColorAppliedByDimension: tile?.style?.barDataColorAppliedBy === "Dimension" && !cascadeFind(tile.xAxisFields, "[*].measureFields", (value) => value.length > 1) && getFieldType(tile["yAxisField"]) !== "date" && isEmpty(tile?.groupField),
                        tooltipOptions,
                        measurementFormatters: formatters.measurementFormatters,
                    })
                );
            },
        ],

        [
            "yAxisKey",
            ({data}, next) =>
                UseState({
                    next: ({value, onChange}) =>
                        next({
                            value: value == null || data[value] == null ? Object.keys(data)[0] : value,
                            onChange,
                        }),
                }),
        ],

        [
            "referenceSeries",
            ({yAxisKey, data, formatters}, next) =>
                next(
                    generateReferenceSeries({
                        tile,
                        theme,
                        formatters,
                        series: tile.style.multipleAxisOption === "IndependentAxes" ? flatten1(Object.values(data).map(({series}) => series)) : data[yAxisKey.value].series,
                    })
                ),
        ],

        [
            "yAxisOptions",
            ({formatters, data, rawData2, referenceSeries, isCompare}, next) =>
                next(
                    getMeasurementAxesOptions({
                        tile,
                        theme,
                        data,
                        rawData: rawData2,
                        referenceSeries,
                        isCompare,
                        styleMeasurementAttr: "xAxis",
                        measurementFormatters: formatters.measurementFormatters,
                    })
                ),
        ],

        [
            "xAxisOptions",
            ({formatters, data, yAxisKey, isCompare, rawData2}, next) =>
                next(
                    getDimensionAxisOptions({
                        tile,
                        theme,
                        rawSeries: rawData2.series.filter((s) => s.stack === yAxisKey.value),
                        series: data[yAxisKey.value].series,
                        isCompare,
                        dimensionAttr: "yAxisField",
                        styleDimensionAttr: "yAxis",
                        dimensionFormatter: formatters.dimensionFormatter,
                    })
                ),
        ],

        [
            "controls",
            ({data, yAxisKey, tileActionControlFilter, rawData, tileFields}, next) => {
                const seriesKeys = Object.keys(data);
                const isIndependentAxes = tile.style.multipleAxisOption == "IndependentAxes";
                const hasMenuOptions = tile.style.showDownloadData || tile.style.showDownloadImage;
                const removeTileControlFilter = tileActionControlFilter?.value?.action?.value && tileActionControlFilter?.value?.tile.id == tile.id;
                const hasAlternativeAxes = !isIndependentAxes && seriesKeys.length > 1;
                const hasCurrencyConversionFailures = rawData.series.filter((s) => s.currencyConversionFailures).length > 0;

                return next(
                    fragments(
                        Watch({
                            value: !!hasAlternativeAxes,
                            onChanged: (changed) => hasSwapAxis?.onChange?.(!!changed),
                            initRun: true,
                        }),

                        <div className="controls">
                            {CurrencyConversionWarning({
                                theme,
                                currencyConversion: getCurrencyConversion({
                                    $type: rawData.$type,
                                    data: rawData,
                                    tileFields,
                                }),
                            })}

                            {BlankDateWarning({
                                isDimensionRealDate: isDimRealDateTime({
                                    tile,
                                    dimensionAttr: "yAxisField",
                                    series: rawData.series,
                                    noLimit: true,
                                }),
                                filters: tileFilters.getValue(),
                            })}

                            {RemoveTileControlFilter({
                                tile,
                                hideBorder: !hasAlternativeAxes && !hasMenuOptions,
                            })}

                            {hasMenuOptions &&
                                MenuOptions({
                                    chartRef,
                                    theme,
                                    tile,
                                    downloadData,
                                    tileFilters,
                                })}

                            {hasAlternativeAxes &&
                                SwapAxis({
                                    tile,
                                    theme,
                                    list: [
                                        {
                                            choices: seriesKeys,
                                            onSelect: (k) => {
                                                yAxisKey.onChange(k);
                                            },
                                            isSelected: (k) => yAxisKey.value === k,
                                            valueToLabel: (key) => tile.style.xAxis[key].label || data[key].label,
                                        },
                                    ],
                                })}
                        </div>
                    )
                );
            },
        ],

        ({rawData, data, controls}, next) =>
            ChartInnerLayout({
                size,
                tile,
                theme,
                next,
                noData: !isDataValid(rawData),
                hasControls: !!controls,
            }),
        [
            "actionMenu",
            ({formatters}, next) =>
                ActionMenu({
                    tile,
                    overrideTile,
                    dimensionFormatter: formatters.dimensionFormatter,
                    disabledTileActions,
                    next,
                }),
        ],

        [
            "onClickPoint",
            ({actionMenu, tileFields, data}, next) => {
                if (!actionMenu.hasActions()) {
                    return next();
                }
                return next(({point, ...other}) => {
                    const dimensionField = tileFields.find((f) => f.$type == "DimensionTileField");
                    const dimensionValue = point.name ?? point.category;

                    const groupField = tileFields.find((f) => f.$type == "CategoryTileField");
                    let groupValue = null;
                    let fieldToValue = {
                        [dimensionField.id]: dimensionValue,
                    };

                    if (groupField) {
                        groupValue = point.series.name;
                        fieldToValue[groupField.id] = groupValue;
                    }

                    const _fieldToValue = tileFields
                        .filter((f) => f.$type == "MeasureTileField")
                        .reduce((obj, field) => {
                            let items = null;
                            if (groupField) {
                                const series = data[field.id].series;
                                items = series.find((s) => s.name == groupValue)?.data;
                            } else {
                                const key = Object.keys(data).find((k) => k.indexOf(field.id) >= 0);
                                items = data[key].series.find((s) => s.tileFieldID == field.id)?.data;
                            }

                            obj[field.id] = items?.find((d) => d[0] == dimensionValue)?.[1] ?? null;
                            return obj;
                        }, {});

                    const tileActionData = point.series?.userOptions?.tileActionData;

                    tileActionData?.columns.forEach((c, index) => {
                        fieldToValue[c.tileFieldID] = tileActionData.data[index];
                    });

                    actionMenu.show({
                        point,
                        ...other,
                        fieldToValue: {
                            ...fieldToValue,
                            ..._fieldToValue,
                        },
                    });
                });
            },
        ],

        ({selectedTimezone}, next) =>
            IgnoreUpdate({
                next,
                props: {timezoneId: selectedTimezone?.value},
                when: ({timezoneId}) => timezoneId != selectedTimezone?.value,
            }),

        ({yAxisKey, data, rawData2, referenceSeries, isCompare, yAxisOptions, xAxisOptions, formatters, actionMenu, onClickPoint, controls}) => {
            const isIndependentAxes = tile.style.multipleAxisOption == "IndependentAxes";
            let options = {
                ...getHorizontalBarOptions({
                    tile,
                    theme,
                    isCompare,
                    rawData: rawData2,
                    formatters,
                    onClickPoint,
                }),
                xAxis: xAxisOptions,
                yAxis: !isIndependentAxes ? yAxisOptions[yAxisKey.value].options : Object.values(yAxisOptions).map(({options}) => options),
                series: [...(!isIndependentAxes ? data[yAxisKey.value].series : flatten1(Object.values(data).map(({series}) => series))), ...(referenceSeries || [])],
            };

            const currentXAxis = Array.isArray(options.xAxis) ? options.xAxis.find((item) => item.visible !== false) : options.xAxis;

            if (actionMenu?.hasActions() && currentXAxis.labels.enabled) {
                currentXAxis.labels.events = {
                    click: function ({x, y}) {
                        actionMenu?.show?.({
                            x,
                            y,
                            point: {category: this.pos, y: null},
                            onlyDimensionField: true,
                            chart: this.chart,
                        });
                    },
                };
            }

            return (
                <div
                    className={cx("horizontal-bar-4w6", !tile?.style?.title.show && "hide-title")}
                    key={getRefreshKey({
                        tile,
                        theme,
                        tileFiltersValue: tileFilters.getValue(),
                        key: yAxisKey.value,
                    })}
                >
                    <div className="chart">
                        <RenderChart
                            {...{
                                chartRef,
                                options,
                                size,
                                theme,
                                tile,
                                formatters,
                            }}
                        />
                    </div>

                    {controls}
                </div>
            );
        }
    );

const getRefreshKey = ({tile, theme, tileFiltersValue, key}) => JSON.stringify([keepOnly(tile, ["id", "style", "filters", "groupField", "yAxisField", "xAxisFields", "limit", "sort", "categorySort", "referenceLines"]), theme.dataVisualization.toolTipsAndLegends, key, tileFiltersValue]);
